import { createColumnHelper } from '@tanstack/react-table';
import { lt } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import {
    ConvertedCurrencyValue,
    Dash,
    LocalizedDateFromUtc,
    LocalizedDateTimeFromUtc,
    SpacedDash,
    TooltippedIcon,
} from '@mews-commander/ui';
import {
    PaymentPlanPaymentTableRecord,
    PaymentPlanPaymentType,
    PaymentPlanTableRecordPaymentActions,
    PaymentTableRecordPaymentActions,
} from '@mews-commander/data-access';
import { IconName, Stack, Tag } from '@optimus-web/core';
import { PaymentTypeCell } from '../Payments/PaymentTypeCell';
import { PaymentsTableBillCell } from '../Payments/PaymentsTableBillCell';
import { PaymentsTableStatusCell } from '../Payments/PaymentsTableStatusCell';
import { PaymentMethodType } from '../PaymentPlans/PaymentMethodType';
import { WithPaymentPlanId } from '../PaymentPlans/types';
import { CreatedCell } from '../Payments/CreatedCell';
import { PaymentPlanDetailsOverviewTableCellActions } from './PaymentPlanDetailsOverviewTableCellActions';

const PaymentMethodCell = ({
    paymentRecord,
    accountId,
}: { paymentRecord: PaymentPlanPaymentTableRecord } & WithAccountId) => {
    // TODO: PaymentMethodType and PaymentTypeCell should be merged together to diplay same results, FINTECH-3711
    const {
        id,
        amount,
        paymentPlanPaymentData: { discriminator, value },
    } = paymentRecord;

    if (discriminator === PaymentPlanPaymentType.Canceled || discriminator === PaymentPlanPaymentType.Future) {
        const { paymentMethodData } = value;
        return paymentMethodData ? (
            <PaymentMethodType paymentMethodData={paymentMethodData} accountId={accountId} />
        ) : (
            <Dash />
        );
    }

    return <PaymentTypeCell payment={{ ...value, id, amount }} accountId={accountId} />;
};

const columnHelper = createColumnHelper<PaymentPlanPaymentTableRecord>();

export const getPaymentPlanDetailsOverviewTableMapping = ({
    accountId,
    paymentPlanId,
}: WithPaymentPlanId & WithAccountId) => [
    columnHelper.accessor('id', {
        header: () => lt('PaymentMethod'),
        cell: ({ row: { original: paymentRecord } }) => (
            <PaymentMethodCell paymentRecord={paymentRecord} accountId={accountId} />
        ),
    }),
    columnHelper.accessor('paymentPlanPaymentData', {
        id: 'bill',
        header: () => lt('Bill'),
        cell: ({ row: { original: paymentRecord } }) => {
            const { discriminator } = paymentRecord.paymentPlanPaymentData;
            if (discriminator === PaymentPlanPaymentType.Canceled || discriminator === PaymentPlanPaymentType.Future) {
                return <Dash />;
            }
            return <PaymentsTableBillCell accountId={accountId} payment={paymentRecord.paymentPlanPaymentData.value} />;
        },
    }),
    columnHelper.accessor('paymentPlanPaymentData', {
        id: 'status',
        header: () => lt('Status'),
        cell: ({ row: { original: paymentRecord } }) => {
            const { discriminator, value } = paymentRecord.paymentPlanPaymentData;

            switch (discriminator) {
                case PaymentPlanPaymentType.Future:
                    return (
                        <Tag variant="primary" outline>
                            {lt('PaymentPlanDetailsOverviewTableStatusFuturePayment')}
                        </Tag>
                    );
                case PaymentPlanPaymentType.Historical:
                    return (
                        <PaymentsTableStatusCell transactionDetails={value.transactionDetails} state={value.state} />
                    );

                case PaymentPlanPaymentType.Canceled:
                    return <PaymentsTableStatusCell state={value.state} />;

                default:
                    return <Dash />;
            }
        },
    }),
    columnHelper.accessor('paymentPlanPaymentData', {
        id: 'date',
        header: () => lt('Date'),
        cell: ({ row: { original: paymentRecord } }) => {
            const { paymentPlanPaymentData } = paymentRecord;

            if (paymentPlanPaymentData.discriminator === PaymentPlanPaymentType.Historical) {
                const { createdUtc } = paymentPlanPaymentData.value;
                return (
                    <CreatedCell
                        createdUtc={createdUtc}
                        creatorProfileName={paymentPlanPaymentData.value.creatorProfileName}
                    />
                );
            } else if (
                paymentPlanPaymentData.discriminator === PaymentPlanPaymentType.Future ||
                paymentPlanPaymentData.discriminator === PaymentPlanPaymentType.Canceled
            ) {
                const { dueDateUtc } = paymentPlanPaymentData.value;
                return <LocalizedDateTimeFromUtc utcString={dueDateUtc} />;
            }
            return <Dash />;
        },
    }),
    columnHelper.accessor('paymentPlanPaymentData', {
        id: 'consumptionDates',
        header: () => lt('PaymentPlansOverviewTablePeriodCoveredColumnHeader'),
        cell: ({ row: { original: paymentRecord } }) => {
            const { discriminator, value } = paymentRecord.paymentPlanPaymentData;
            const { consumptionDates } = value;

            if (!consumptionDates?.startDateUtc || !consumptionDates?.endDateUtc) {
                return <Dash />;
            }

            return (
                <Stack itemAlignment="center" spacing="100">
                    <Stack>
                        <LocalizedDateFromUtc utcString={consumptionDates.startDateUtc} />
                        {SpacedDash}
                        <LocalizedDateFromUtc utcString={consumptionDates.endDateUtc} />
                    </Stack>
                    {(discriminator === PaymentPlanPaymentType.Future ||
                        discriminator === PaymentPlanPaymentType.Historical) &&
                        value.initialPaymentRequestId && (
                            <TooltippedIcon
                                size="small"
                                value={IconName.Info}
                                tooltip={lt('PaymentPlansOverviewTablePeriodCoveredFirstPaymentTooltip')}
                            />
                        )}
                </Stack>
            );
        },
    }),
    columnHelper.accessor('amount', {
        header: () => lt('Amount'),
        cell: ({
            row: {
                original: { amount },
            },
        }) => <ConvertedCurrencyValue amount={amount} />,
    }),
    columnHelper.accessor('paymentPlanPaymentData', {
        id: 'actions',
        header: () => null,
        meta: {
            align: 'end',
        },
        cell: ({ row: { original: paymentRecord } }) => {
            const { discriminator, value } = paymentRecord.paymentPlanPaymentData;

            if (discriminator === PaymentPlanPaymentType.Canceled) {
                return null;
            }

            const amount = { currencyCode: paymentRecord.amount.currency, value: paymentRecord.amount.value };

            if (discriminator === PaymentPlanPaymentType.Future) {
                return (
                    <PaymentPlanDetailsOverviewTableCellActions
                        id={paymentRecord.id}
                        paymentPlanId={paymentPlanId}
                        hasRefundAction={value.actions?.includes(PaymentPlanTableRecordPaymentActions.Refund)}
                        hasCancelAction={value.actions?.includes(PaymentPlanTableRecordPaymentActions.Cancel)}
                        simpleAmount={amount}
                        accountId={accountId}
                    />
                );
            }

            return (
                <PaymentPlanDetailsOverviewTableCellActions
                    id={paymentRecord.id}
                    paymentPlanId={paymentPlanId}
                    hasRefundAction={value.actions?.includes(PaymentTableRecordPaymentActions.Refund)}
                    simpleAmount={amount}
                    accountId={accountId}
                />
            );
        },
    }),
];
